import classNames from 'classnames';
import Spinner from 'components/Spinner';
import {useRouteTracking} from './useRouteTracking';
import RouteTrackingMap from './RouteTrackingMap';
import {RouteTrackingDelivery} from 'types/route-tracking';
import {CheckIcon, CloseIcon, ClockIcon, DurationIcon} from './icons';
import styles from './RouteTracking.module.scss';
import {LocationIcon} from 'pages/RouteHistory/components/OrderDetail/icons';
import {getDevicePlatform} from 'utils/get-device-platform';
import AppLogo from 'assets/mobile-logo.png';
import Button from 'components/Button';
import {APP_STORE_URL, PLAY_STORE_URL} from 'constants/url';
type Props = {
  id: string;
};

type DeliveryStatus = 'new' | 'delivered' | 'skipped';

function getDeliveryStatus(delivery: RouteTrackingDelivery): DeliveryStatus {
  if (delivery.delivered_at) return 'delivered';
  if (delivery.skip_reason) return 'skipped';
  return 'new';
}

function RouteTracking({id}: Props) {
  const {data, isLoading} = useRouteTracking({id});

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner />
      </div>
    );
  }

  if (!data) return <div>No data found</div>;

  return (
    <div className={classNames(styles.container, styles.withMap)}>
      <div className={styles.mapSection}>
        <RouteTrackingMap route={data} />
      </div>

      <div className={styles.contentSection}>
        <div className={styles.statsBar}>
          <div className={styles.statItem}>
            <div className={styles.statLabel}>Total</div>
            <div className={styles.statValue}>
              {data.total_deliveries_count}
            </div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statLabel}>Delivered</div>
            <div className={styles.statValue}>
              {data.completed_deliveries_count}
            </div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statLabel}>Skipped</div>
            <div className={styles.statValue}>
              {data.canceled_deliveries_count}
            </div>
          </div>
        </div>

        <div className={styles.deliveryList}>
          <div className={styles.statusSection}>
            {data?.deliveries.map((delivery, index) => (
              <DeliveryCard
                key={delivery.number}
                delivery={delivery}
                index={index}
                status={getDeliveryStatus(delivery)}
              />
            ))}
          </div>
        </div>
        {/* Smart app install banner */}
        <SmartAppInstallBanner />
      </div>
    </div>
  );
}

type DeliveryCardProps = {
  delivery: RouteTrackingDelivery;
  status: DeliveryStatus;
  index: number;
};

function DeliveryCard({delivery, status, index}: DeliveryCardProps) {
  const formatTime = (time: string | null) => {
    if (!time) return '';
    return time;
  };

  return (
    <div className={styles.deliveryCard}>
      <div className={styles.cardHeader}>
        <div className={styles.cardLeft}>
          <div
            className={classNames(
              styles.deliveryBadge,
              styles[`${status}-badge`]
            )}
          >
            Stop {index + 1} • #{delivery.number}
          </div>
          <div className={styles.orderTypeLabel}>{delivery.order_type}</div>
        </div>
        <div className={classNames(styles.statusIcon, styles[status])}>
          {status === 'delivered' && <CheckIcon />}
          {status === 'skipped' && <CloseIcon />}
          {status === 'new' && <ClockIcon />}
        </div>
      </div>

      <div className={styles.cardDetails}>
        {delivery.arrival_date && (
          <div className={styles.detailRow}>
            <LocationIcon />
            <span className={styles.detailText}>
              {delivery.arrival_date}
              {delivery.arrival_time && ` at ${delivery.arrival_time}`}
            </span>
          </div>
        )}

        {delivery.from_time && delivery.to_time && (
          <div className={styles.detailRow}>
            <ClockIcon />
            <span className={styles.detailText}>
              {formatTime(delivery.from_time)} - {formatTime(delivery.to_time)}
            </span>
          </div>
        )}

        {delivery.duration && (
          <div className={styles.detailRow}>
            <DurationIcon />
            <span className={styles.detailText}>{delivery.duration}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function SmartAppInstallBanner() {
  const handleInstall = () => {
    const platform = getDevicePlatform();
    if (platform === 'iOS') {
      window.open(APP_STORE_URL, '_blank');
      return;
    } else {
      window.open(PLAY_STORE_URL, '_blank');
      return;
    }
  };
  return (
    <div className={styles.smartAppInstallBanner}>
      <img
        src={AppLogo}
        alt="Smart App Install Banner"
        className={styles.smartAppInstallBannerLogo}
      />
      <div className={styles.smartAppInstallBannerContent}>
        <div className={styles.smartAppInstallBannerTitle}>Jobs App</div>
        <div className={styles.smartAppInstallBannerDescription}>
          Get the app to track your order and get real-time updates.
        </div>
      </div>
      <Button color="green" size="lg" onClick={handleInstall}>
        Install
      </Button>
    </div>
  );
}

export default RouteTracking;
